import { FEATURE_FLAGS } from './core/config/feature-flags';
import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { permissionGuard } from './core/guards/permission-guard';
import { AppShell } from './core/layout/app-shell/app-shell';

export const routes: Routes = [
  {
    path: 'auth/redirect',
    loadComponent: () =>
      import('./features/auth/pages/auth-redirect-page/auth-redirect-page').then(
        (module) => module.AuthRedirectPage,
      ),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/pages/login-page/login-page').then(
        (module) => module.LoginPage,
      ),
  },
  {
    path: '',
    component: AppShell,
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      ...(FEATURE_FLAGS.showDevelopmentTools
        ? [{
            path: 'development',
            loadComponent: () =>
              import('./features/development/pages/development-page/development-page').then(
                (module) => module.DevelopmentPage,
              ),
          }]
        : []),
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        data: { permission: { key: 'dashboard', level: 'read' } },
        loadComponent: () =>
          import('./features/dashboard/pages/dashboard-page/dashboard-page').then(
            (module) => module.DashboardPage,
          ),
      },
      {
        path: 'products',
        canActivate: [permissionGuard],
        data: { permission: { key: 'products', level: 'read' } },
        loadComponent: () =>
          import('./features/products/pages/products-page/products-page').then(
            (module) => module.ProductsPage,
          ),
      },
      {
        path: 'inventory',
        canActivate: [permissionGuard],
        data: { permission: { key: 'inventory', level: 'read' } },
        loadComponent: () =>
          import('./features/inventory/pages/inventory-page/inventory-page').then(
            (module) => module.InventoryPage,
          ),
      },
      {
        path: 'movements',
        canActivate: [permissionGuard],
        data: { permission: { key: 'movements', level: 'read' } },
        loadComponent: () =>
          import('./features/movements/pages/movements-page/movements-page').then(
            (module) => module.MovementsPage,
          ),
      },
      {
        path: 'reports',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/reports-page/reports-page').then(
            (module) => module.ReportsPage,
          ),
      },
      {
        path: 'reports/kardex',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/product-kardex-page/product-kardex-page').then(
            (module) => module.ProductKardexPage,
          ),
      },
      {
        path: 'reports/inventory-valuation',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/inventory-valuation-page/inventory-valuation-page').then(
            (module) => module.InventoryValuationPage,
          ),
      },
      {
        path: 'reports/inventory-by-warehouse',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/inventory-by-warehouse-page/inventory-by-warehouse-page').then(
            (module) => module.InventoryByWarehousePage,
          ),
      },
      {
        path: 'reports/low-stock',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/low-stock-page/low-stock-page').then(
            (module) => module.LowStockPage,
          ),
      },
      {
        path: 'reports/movements-by-type',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/movements-by-type-page/movements-by-type-page').then(
            (module) => module.MovementsByTypePage,
          ),
      },
      {
        path: 'reports/production-vs-sales',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/production-sales-page/production-sales-page').then(
            (module) => module.ProductionSalesPage,
          ),
      },
      {
        path: 'reports/transfers',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/transfers-page/transfers-page').then(
            (module) => module.TransfersPage,
          ),
      },
      {
        path: 'reports/client-outbound',
        canActivate: [permissionGuard],
        data: { permission: { key: 'reports', level: 'read' } },
        loadComponent: () =>
          import('./features/reports/pages/client-outbound-page/client-outbound-page').then(
            (module) => module.ClientOutboundPage,
          ),
      },
      {
        path: 'settings',
        canActivate: [permissionGuard],
        data: { permission: { key: 'settings', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/pages/settings-page/settings-page').then(
            (module) => module.SettingsPage,
          ),
      },
      {
        path: 'settings/users',
        canActivate: [permissionGuard],
        data: { permission: { key: 'organizationUsers', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/users/pages/settings-users-page/settings-users-page').then(
            (module) => module.SettingsUsersPage,
          ),
      },
      {
        path: 'settings/catalogs/warehouses',
        canActivate: [permissionGuard],
        data: { permission: { key: 'warehouses', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/warehouses/pages/warehouses-page/warehouses-page').then(
            (module) => module.SettingsWarehousesPage,
          ),
      },
      {
        path: 'settings/catalogs/categories',
        canActivate: [permissionGuard],
        data: { permission: { key: 'categories', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/categories/pages/categories-page/categories-page').then(
            (module) => module.SettingsCategoriesPage,
          ),
      },
      {
        path: 'settings/catalogs/units',
        canActivate: [permissionGuard],
        data: { permission: { key: 'units', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/units/pages/units-page/units-page').then(
            (module) => module.SettingsUnitsPage,
          ),
      },
      {
        path: 'settings/catalogs/packaging-types',
        canActivate: [permissionGuard],
        data: { permission: { key: 'packagingTypes', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/packaging-types/pages/packaging-types-page/packaging-types-page').then(
            (module) => module.SettingsPackagingTypesPage,
          ),
      },
      {
        path: 'settings/exchange-rate',
        canActivate: [permissionGuard],
        data: { permission: { key: 'exchangeRates', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/exchange-rate/pages/exchange-rate-page/exchange-rate-page').then(
            (module) => module.ExchangeRatePage,
          ),
      },
      {
        path: 'settings/catalogs/movement-types',
        canActivate: [permissionGuard],
        data: { permission: { key: 'movementTypes', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/movement-types/pages/movement-types-page/movement-types-page').then(
            (module) => module.SettingsMovementTypesPage,
          ),
      },
      {
        path: 'settings/catalogs/customer-types',
        canActivate: [permissionGuard],
        data: { permission: { key: 'customerTypes', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/customer-types/pages/customer-types-page/customer-types-page').then(
            (module) => module.SettingsCustomerTypesPage,
          ),
      },
      {
        path: 'settings/catalogs/providers',
        canActivate: [permissionGuard],
        data: { permission: { key: 'providers', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/providers/pages/providers-page/providers-page').then(
            (module) => module.SettingsProvidersPage,
          ),
      },
      {
        path: 'settings/catalogs/clients',
        canActivate: [permissionGuard],
        data: { permission: { key: 'clients', level: 'read' } },
        loadComponent: () =>
          import('./features/settings/catalogs/clients/pages/clients-page/clients-page').then(
            (module) => module.SettingsClientsPage,
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
